/**
 * MillOrders - Orders Overview for Mill Portal
 * Shows orders by timeframe with pending modifications requiring approval.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { millOrdersAPI, OrderOverview, ModificationOverview } from '../../services/millAPI';
import MobileScrollTable from '../../components/common/MobileScrollTable';
import './Mill.css';

type ViewType = 'today' | 'this_week' | 'next_week' | 'all_pending';

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return 'Not set';
  return new Date(dateStr).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatRelativeTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDate(dateStr);
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'PENDING': return '#f59e0b';
    case 'CONFIRMED': return '#3b82f6';
    case 'IN_PRODUCTION': return '#8b5cf6';
    case 'PRODUCTION': return '#8b5cf6';
    case 'MILLED': return '#10b981';
    case 'PACKAGING': return '#06b6d4';
    case 'READY': return '#22c55e';
    case 'SHIPPED': return '#6b7280';
    case 'DELIVERED': return '#059669';
    case 'PENDING_MODIFICATION': return '#ef4444';
    default: return '#9ca3af';
  }
};

const MillOrders: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeView, setActiveView] = useState<ViewType>(
    (searchParams.get('view') as ViewType) || 'this_week'
  );
  const [orders, setOrders] = useState<OrderOverview[]>([]);
  const [ordersNeedingAttention, setOrdersNeedingAttention] = useState<OrderOverview[]>([]);
  const [statusCounts, setStatusCounts] = useState<{ [key: string]: number }>({});
  const [modifications, setModifications] = useState<ModificationOverview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedModification, setSelectedModification] = useState<ModificationOverview | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showModificationsPanel, setShowModificationsPanel] = useState(false);

  // Helper to safely extract array from API response
  const extractArray = (data: any): any[] => {
    if (Array.isArray(data)) return data;
    if (data?.results && Array.isArray(data.results)) return data.results;
    if (data?.modifications && Array.isArray(data.modifications)) return data.modifications;
    return [];
  };

  // Helper to extract orders from various API response formats
  const extractOrders = (data: any): OrderOverview[] => {
    // If data is already an array
    if (Array.isArray(data)) return data;

    // DRF pagination: { results: [...] }
    if (data?.results && Array.isArray(data.results)) return data.results;

    // Direct orders array: { orders: [...] }
    if (data?.orders && Array.isArray(data.orders)) return data.orders;

    // Orders by date dictionary: { orders_by_date: { "2025-01-01": [...], ... } }
    if (data?.orders_by_date && typeof data.orders_by_date === 'object') {
      const allOrders: OrderOverview[] = [];
      for (const dateKey of Object.keys(data.orders_by_date).sort()) {
        const dateOrders = data.orders_by_date[dateKey];
        if (Array.isArray(dateOrders)) {
          allOrders.push(...dateOrders);
        }
      }
      return allOrders;
    }

    // Data array: { data: [...] }
    if (data?.data && Array.isArray(data.data)) return data.data;

    return [];
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [ordersRes, modsRes] = await Promise.all([
        millOrdersAPI.getOverview({ timeframe: activeView }),
        millOrdersAPI.getPendingModifications(),
      ]);

      // Safely extract data with fallbacks
      const ordersData = ordersRes.data || {};
      const modsData = modsRes.data || {};

      // Extract orders from various response formats
      const allOrders = extractOrders(ordersData);
      console.log('Loaded orders:', allOrders.length, 'from response:', Object.keys(ordersData));
      setOrders(allOrders);

      // Orders needing attention = orders with pending modifications or rush orders
      const attentionOrders = allOrders.filter(o => o.has_pending_modification || o.is_rush);
      setOrdersNeedingAttention(attentionOrders);

      setStatusCounts(ordersData.status_counts || {});
      setModifications(extractArray(modsData));
    } catch (err: any) {
      console.error('Error loading orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [activeView]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setSearchParams({ view: activeView });
  }, [activeView, setSearchParams]);

  const handleApprove = async (modId: number) => {
    if (processingId) return;
    setProcessingId(modId);
    try {
      await millOrdersAPI.approveModification(modId);
      await loadOrders();
      setSelectedModification(null);
    } catch (err) {
      console.error('Failed to approve:', err);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (modId: number, reason?: string) => {
    if (processingId) return;
    setProcessingId(modId);
    try {
      await millOrdersAPI.rejectModification(modId, { reason });
      await loadOrders();
      setSelectedModification(null);
    } catch (err) {
      console.error('Failed to reject:', err);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="mill-page">
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mill-page mill-orders-page">
      {/* Header */}
      <div className="mill-page-header">
        <h1>Orders Overview</h1>
        <div className="header-actions">
          {modifications.length > 0 && (
            <button
              className={`modifications-button ${showModificationsPanel ? 'active' : ''}`}
              onClick={() => setShowModificationsPanel(!showModificationsPanel)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Pending Modifications
              <span className="modifications-badge">{modifications.length}</span>
            </button>
          )}
        </div>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        {(['today', 'this_week', 'next_week', 'all_pending'] as ViewType[]).map(view => (
          <button
            key={view}
            className={`view-tab ${activeView === view ? 'active' : ''}`}
            onClick={() => setActiveView(view)}
          >
            {view === 'today' && 'Today'}
            {view === 'this_week' && 'This Week'}
            {view === 'next_week' && 'Next Week'}
            {view === 'all_pending' && 'All Pending'}
          </button>
        ))}
      </div>

      {/* Status Summary */}
      <div className="status-summary">
        <div className="status-card" style={{ borderLeftColor: '#f59e0b' }}>
          <div className="status-count">{statusCounts.need_to_produce || 0}</div>
          <div className="status-label">Need to Produce</div>
        </div>
        <div className="status-card" style={{ borderLeftColor: '#8b5cf6' }}>
          <div className="status-count">{statusCounts.in_production || 0}</div>
          <div className="status-label">In Production</div>
        </div>
        <div className="status-card" style={{ borderLeftColor: '#22c55e' }}>
          <div className="status-count">{statusCounts.ready_to_ship || 0}</div>
          <div className="status-label">Ready to Ship</div>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="orders-layout">
        {/* Main Orders List */}
        <div className="orders-main">
          {/* Attention Required Section */}
          {ordersNeedingAttention.length > 0 && (
            <div className="attention-section">
              <h2 className="section-title attention-title">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Needs Attention ({ordersNeedingAttention.length})
              </h2>
              <div className="orders-grid">
                {ordersNeedingAttention.map(order => (
                  <div key={order.id} className={`order-card attention ${order.pending_modification ? 'on-hold' : ''}`}>
                    <div className="order-card-header">
                      <div className="order-number">#{order.order_number}</div>
                      {order.is_rush && <span className="rush-badge">RUSH</span>}
                      {order.pending_modification && <span className="hold-badge">ON HOLD</span>}
                      {order.is_bridge_club && <span className="bridge-club-badge">Bridge Club</span>}
                      {order.source === 'farmhand' && <span className="farmhand-badge">Farmhand</span>}
                    </div>
                    <div className="order-customer">{order.customer_name}</div>
                    {order.special_instructions && (
                      <div className="customer-instructions-banner">
                        <strong>📋 Instructions:</strong> {order.special_instructions}
                      </div>
                    )}
                    <div className="order-details">
                      <span className="order-date">
                        Ship: {formatDate(order.delivery_date)}
                      </span>
                      <span className="order-items">{order.items?.length || 0} items</span>
                    </div>
                    <div className="order-footer">
                      <span className="order-status" style={{ color: getStatusColor(order.status) }}>
                        {order.status_display}
                      </span>
                      <button
                        className="view-order-btn"
                        onClick={() => navigate(`/mill/shipping?order=${order.id}`)}
                      >
                        View
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Orders Section */}
          <div className="all-orders-section">
            <h2 className="section-title">All Orders ({orders.length})</h2>
            {orders.length === 0 ? (
              <div className="no-orders">
                <p>No orders for this timeframe.</p>
              </div>
            ) : (
              <div className="orders-table">
                <MobileScrollTable>
                <table>
                  <thead>
                    <tr>
                      <th>Order #</th>
                      <th>Customer</th>
                      <th>Ship Date</th>
                      <th>Items</th>
                      <th>Status</th>
                      <th>Shipping</th>
                      <th>Weight</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map(order => (
                      <tr key={order.id} className={order.pending_modification ? 'on-hold' : ''}>
                        <td>
                          <div className="order-number-cell">
                            #{order.order_number}
                            {order.is_rush && <span className="rush-badge small">RUSH</span>}
                            {order.pending_modification && <span className="hold-badge small">HOLD</span>}
                            {order.is_bridge_club && <span className="bridge-club-badge small">BC</span>}
                            {order.source === 'farmhand' && <span className="farmhand-badge small">FH</span>}
                            {order.special_instructions && (
                              <span className="instructions-badge small" title={order.special_instructions}>📋</span>
                            )}
                          </div>
                        </td>
                        <td>{order.customer_name}</td>
                        <td>{formatDate(order.delivery_date)}</td>
                        <td>{order.items?.length || 0}</td>
                        <td>
                          <span className="status-badge" style={{ backgroundColor: getStatusColor(order.status) }}>
                            {order.status_display}
                          </span>
                        </td>
                        <td>{order.shipping_method_display}</td>
                        <td>{(order.total_weight_lbs || 0).toLocaleString()} lbs</td>
                        <td>
                          <button
                            className="table-action-btn"
                            onClick={() => navigate(`/mill/shipping?order=${order.id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </MobileScrollTable>
              </div>
            )}
          </div>
        </div>

        {/* Modifications Panel (slide-out) */}
        {showModificationsPanel && modifications.length > 0 && (
          <div className="modifications-panel">
            <div className="panel-header">
              <h3>Pending Modifications</h3>
              <button className="panel-close" onClick={() => setShowModificationsPanel(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modifications-list">
              {modifications.map(mod => (
                <div key={mod.id} className="modification-card">
                  <div className="mod-header">
                    <span className="mod-order">#{mod.order_number}</span>
                    <span className="mod-time">{formatRelativeTime(mod.created_at)}</span>
                  </div>
                  <div className="mod-customer">{mod.customer_name}</div>
                  <div className="mod-type">{mod.modification_type_display}</div>
                  <div className="mod-description">{mod.description}</div>
                  <div className="mod-by">
                    Modified by: {mod.modified_by_type === 'CUSTOMER' ? 'Customer' : mod.modified_by_name || 'Staff'}
                  </div>
                  <div className="mod-actions">
                    <button
                      className="mod-btn approve"
                      onClick={() => handleApprove(mod.id)}
                      disabled={processingId === mod.id}
                    >
                      {processingId === mod.id ? '...' : 'Approve'}
                    </button>
                    <button
                      className="mod-btn reject"
                      onClick={() => handleReject(mod.id)}
                      disabled={processingId === mod.id}
                    >
                      {processingId === mod.id ? '...' : 'Reject'}
                    </button>
                    <button
                      className="mod-btn view"
                      onClick={() => navigate(`/mill/shipping?order=${mod.order_id}`)}
                    >
                      View Order
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MillOrders;
